/**
 * ApprovalsContext - Data store for page approval data
 *
 * This provider follows the pattern established in AnnotationsProvider and
 * documented in patterns/component-architecture.md:
 * - Separate read and write contexts to prevent unnecessary re-renders
 * - Server data managed by Apollo GraphQL (useQueryInterop)
 * - UI state managed by React Context
 *
 * Usage:
 *   const { approvalRequest, loading } = useApprovals();
 *   const { startApproval, updateApprovalStatus } = useApprovalsDispatch();
 */

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type FC,
	type ReactNode,
} from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { usePageContentId, usePageState } from '@confluence/page-context';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import {
	PageMode,
	CURRENT_STATUS,
	DRAFT_STATUS,
} from '@confluence/page-utils/entry-points/enums.ts';
import { useCanPerformContentOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import type { ConfluenceWorkflowApplicationChanged } from '@confluence/graphql/entry-points/subscriptionTypes.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import {
	QuickReloadSubscriber,
	ReloadType,
} from '@confluence/quick-reload/entry-points/subscription.ts';

import { APPROVALS_PANE_ID } from './approvalsPaneSidebar.ts';
import type {
	ApprovalsContextCreateWorkflowApplicationMutation$data,
	ApprovalsContextCreateWorkflowApplicationMutation$variables,
} from './__generated__/ApprovalsContextCreateWorkflowApplicationMutation.graphql.ts';
import type {
	ApprovalsContextCreateWorkflowReviewMutation$data,
	ApprovalsContextCreateWorkflowReviewMutation$variables,
} from './__generated__/ApprovalsContextCreateWorkflowReviewMutation.graphql.ts';
import type {
	ApprovalsContextUpdateWorkflowApplicationMutation$data,
	ApprovalsContextUpdateWorkflowApplicationMutation$variables,
	GraphQLContentStatus,
} from './__generated__/ApprovalsContextUpdateWorkflowApplicationMutation.graphql.ts';
import type {
	ApprovalsContextUpdateReviewerDecisionMutation$data,
	ApprovalsContextUpdateReviewerDecisionMutation$variables,
	ConfluenceReviewerDecisionState,
} from './__generated__/ApprovalsContextUpdateReviewerDecisionMutation.graphql.ts';
import type {
	ApprovalsContextUpdateWorkflowReviewMutation$data,
	ApprovalsContextUpdateWorkflowReviewMutation$variables,
} from './__generated__/ApprovalsContextUpdateWorkflowReviewMutation.graphql.ts';
import type {
	ApprovalsContextRemoveContentApprovalReviewerMutation$data,
	ApprovalsContextRemoveContentApprovalReviewerMutation$variables,
} from './__generated__/ApprovalsContextRemoveContentApprovalReviewerMutation.graphql.ts';
import type {
	ApprovalsContextApprovalsDataQuery$data,
	ApprovalsContextApprovalsDataQuery$variables,
	ConfluenceWorkflowReviewStatus,
	ConfluencePrincipalType as PrincipalType,
	ConfluenceReviewerDecisionState as ReviewerDecisionStatus,
} from './__generated__/ApprovalsContextApprovalsDataQuery.graphql.ts';

const DRAFT_CONTENT_STATUS: GraphQLContentStatus = 'DRAFT';
const CURRENT_CONTENT_STATUS: GraphQLContentStatus = 'CURRENT';

// GraphQL Mutations - AGG Ready
// Mutation: Create Workflow Application
const createWorkflowApplicationMutation = graphql`
	mutation ApprovalsContextCreateWorkflowApplicationMutation(
		$cloudId: ID!
		$createWorkflowApplicationRequestInput: ConfluenceCreateWorkflowApplicationRequestInput!
	) {
		confluence_createWorkflowApplication(
			cloudId: $cloudId
			createWorkflowApplicationRequestInput: $createWorkflowApplicationRequestInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			workflowStatus {
				id
				allowsReviews
				displayProperties {
					name
					description
					color
					icon
				}
			}
		}
	}
`;

// Mutation: Create Workflow Review
const createWorkflowReviewMutation = graphql`
	mutation ApprovalsContextCreateWorkflowReviewMutation(
		$cloudId: ID!
		$createContentApprovalRequestInput: ConfluenceCreateContentApprovalRequestInput!
	) {
		confluence_createContentApproval(
			cloudId: $cloudId
			createContentApprovalRequestInput: $createContentApprovalRequestInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			contentApproval {
				id
				workflowApplicationId
				reviewStatus
				comment
				reviewers {
					principalType
					principalId
					isRequired
					user {
						id
						displayName
						photos {
							isPrimary
							value
						}
						emails {
							isPrimary
							value
						}
					}
				}
			}
		}
	}
`;

// Mutation: Update Workflow Application
const updateWorkflowApplicationMutation = graphql`
	mutation ApprovalsContextUpdateWorkflowApplicationMutation(
		$cloudId: ID!
		$updateWorkflowApplicationRequestInput: ConfluenceUpdateWorkflowApplicationRequestInput!
	) {
		confluence_updateWorkflowApplication(
			cloudId: $cloudId
			updateWorkflowApplicationRequestInput: $updateWorkflowApplicationRequestInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
		}
	}
`;

// Mutation: Update Reviewer Decision (Approve/Deny)
const updateReviewerDecisionMutation = graphql`
	mutation ApprovalsContextUpdateReviewerDecisionMutation(
		$cloudId: ID!
		$confluenceUpdateReviewerDecisionInput: ConfluenceUpdateReviewerDecisionInput!
	) {
		confluence_updateReviewerDecision(
			cloudId: $cloudId
			confluenceUpdateReviewerDecisionInput: $confluenceUpdateReviewerDecisionInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
		}
	}
`;

// Mutation: Update Workflow Review (add/remove reviewers, update status)
const updateWorkflowReviewMutation = graphql`
	mutation ApprovalsContextUpdateWorkflowReviewMutation(
		$cloudId: ID!
		$updateContentApprovalGraphQLInput: ConfluenceUpdateContentApprovalRequestInput!
	) {
		confluence_updateContentApproval(
			cloudId: $cloudId
			updateContentApprovalGraphQLInput: $updateContentApprovalGraphQLInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			contentApproval {
				id
				reviewStatus
				comment
				reviewers {
					principalType
					principalId
					isRequired
					user {
						id
						displayName
						photos {
							isPrimary
							value
						}
						emails {
							isPrimary
							value
						}
					}
				}
			}
		}
	}
`;

// Mutation: Resolve Approval Agent
const resolveApprovalAgentMutation = graphql`
	mutation ApprovalsContextResolveApprovalAgentMutation($cloudId: ID!) {
		confluence_resolveApprovalAgent(cloudId: $cloudId) {
			success
			agent {
				id
			}
			errors {
				message
			}
		}
	}
`;

// Mutation: Remove Content Approval Reviewer
const removeContentApprovalReviewerMutation = graphql`
	mutation ApprovalsContextRemoveContentApprovalReviewerMutation(
		$cloudId: ID!
		$removeContentApprovalReviewerInput: ConfluenceRemoveContentApprovalReviewerInput!
	) {
		confluence_removeContentApprovalReviewer(
			cloudId: $cloudId
			removeContentApprovalReviewerInput: $removeContentApprovalReviewerInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
		}
	}
`;

// GraphQL Query - Combined: active workflow application + available workflow statuses
// Single query for preloading and runtime; fetches both confluence_getActiveWorkflowApplication
// and confluence_contentApprovalStatuses (transitionType: null returns all statuses)
const approvalsDataQuery = graphql`
	query ApprovalsContextApprovalsDataQuery(
		$cloudId: ID!
		$contentId: ID!
		$contentStatus: String!
		$otherContentStatus: String!
		$transitionType: ConfluenceApprovalTransitionType
	) {
		content(id: $contentId) {
			nodes {
				status
				version {
					number
				}
			}
		}
		confluence_contentApprovalStatuses(cloudId: $cloudId, transitionType: $transitionType) {
			id
			allowsReviews
			displayProperties {
				name
				color
			}
		}
		confluence_getActiveWorkflowApplicationForOtherStatus: confluence_getActiveWorkflowApplication(
			cloudId: $cloudId
			contentId: $contentId
			contentStatus: $otherContentStatus
			workflowId: "1"
			fetchWorkflowReview: true
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			workflowReviewWithDecisions {
				id
				reviewStatus
			}
		}
		confluence_getActiveWorkflowApplication(
			cloudId: $cloudId
			contentId: $contentId
			contentStatus: $contentStatus
			workflowId: "1"
			fetchWorkflowReview: true
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			workflowApplication {
				id
				workflowId
				contentId
				contentVersion
				contentStatus
				workflowStatusId
				isActive
				creator {
					id
					displayName
					isActive
					photos {
						value
						isPrimary
					}
				}
				lastModificationDate {
					value
				}
				modifier {
					id
					displayName
					isActive
					photos {
						value
						isPrimary
					}
				}
			}
			workflowReviewWithDecisions {
				id
				creatorAaid
				creationDate {
					value
				}
				reviewStatus
				reviewComment
				reviewLastModified {
					value
				}
				creator {
					id
					displayName
					isActive
					photos {
						value
						isPrimary
					}
				}
				reviewerDecisions {
					decisionId
					reviewerId
					required
					principalType
					principalId
					reviewerDecisionStatus
					comment
					lastModifiedDate {
						value
					}
					user {
						id
						displayName
						isActive
						photos {
							isPrimary
							value
						}
					}
				}
			}
		}
	}
`;

/**
 * Data structure for user information in content state updates.
 * Used by UserUpdateInfo component and ContentStateInfoPopup.
 * This is the canonical shape for user metadata in content state contexts.
 */
export type UserUpdateInfoData = {
	displayName: string;
	avatarUrl?: string;
	userId: string;
};

export type ApprovalStatus = ConfluenceWorkflowReviewStatus;

export type Approver = {
	decisionId: string;
	reviewerId: string;
	principalId: string;
	principalType: PrincipalType;
	name: string;
	avatarUrl?: string;
	email?: string;
	status: ConfluenceReviewerDecisionState;
	decisionComment?: string;
	isRequired: boolean;
	responseLastModifiedDate?: string;
};

/** Workflow application data from the active workflow application query */
export type ApprovalRequestApplication = {
	contentId: string;
	contentVersion: number;
	contentStatus: string;
	applicationId: string;
	isActive: boolean;
	workflowStatusId: string;
	applicationLastModifiedBy?: UserUpdateInfoData;
	applicationLastModifiedDate?: string;
};

/** Review data (requestor, review metadata, approvers). Null when there is an application but no review yet. */
export type ApprovalRequestReview = {
	reviewId: string;
	reviewCreationDate: string;
	requestorId: string;
	requestorName: string;
	requestorAvatarUrl?: string;
	reviewLastModifiedDate: string;
	reviewComment?: string;
	reviewStatus: ApprovalStatus;
	approvers: Approver[];
};

export type ApprovalRequest = {
	application: ApprovalRequestApplication;
	review: ApprovalRequestReview | null;
};

export type WorkflowStatus = {
	id: string;
	name: string;
	color?: string;
	allowsReviews: boolean;
};

export type ApprovalsContextValue = {
	// Query data
	approvalRequest: ApprovalRequest | null;
	approvalRequestLoading: boolean;

	// Available workflow statuses
	availableWorkflowStatuses: WorkflowStatus[];

	// Content identifiers (for analytics and cross-component reference)
	contentId: string;
	contentStatus: GraphQLContentStatus;

	// Computed values
	isCurrentUserRequestor: boolean;
	isCurrentUserApprover: boolean;
	currentUserApprover: Approver | null;
	approvalProgress: { current: number; total: number };

	// Permissions (pushed from EditorContentStateComponent)
	hasEditPermission: boolean;

	/**
	 * True when the draft is locked for editing: content is draft and approval is in progress
	 * (PENDING), approved (APPROVED), or denied (DENIED). Used to prevent Rovo from editing
	 * the page in this state (via isViewMode).
	 */
	isDraftLockedForEditing: boolean;

	/**
	 * The review status of the opposite content mode (Edit ↔ View).
	 * Used to show a warning when the other mode has an active, pending review.
	 */
	otherModeReviewStatus: ApprovalStatus | null;
};

export type StartApprovalVariables = {
	approvers: Array<{
		principalId: string;
		principalType: PrincipalType;
		isRequired: boolean;
	}>;
	message?: string;
};

export type UpdateWorkflowApplicationVariables = {
	contentId: string;
	contentStatus: string;
	version: number;
	workflowStatusId: string;
	isActive: boolean;
};

export type UpdateApprovalStatusVariables = {
	reviewerId: string;
	status: Extract<ReviewerDecisionStatus, 'REVIEWER_APPROVED' | 'REVIEWER_DENIED'>;
	comment?: string;
};

export type AddApproverVariables = {
	workflowApplicationId: string;
	contentApprovalReviewId: string;
	approvers: Array<{
		principalId: string;
		principalType: PrincipalType;
		isRequired: boolean;
	}>;
};

export type RemoveApproverVariables = {
	principalId: string;
};

export type CancelApprovalVariables = {
	workflowApplicationId: string;
	contentApprovalReviewId: string;
};

export type CreateWorkflowApplicationResult = {
	workflowStatusId: string;
	name: string;
	allowsReviews: boolean;
};

export type ApprovalsDispatchContextValue = {
	// Mutations
	createWorkflowApplication: (status: WorkflowStatus) => Promise<CreateWorkflowApplicationResult>;
	cancelWorkflowApplication: (
		variables?: Omit<UpdateWorkflowApplicationVariables, 'isActive'>,
	) => Promise<void>;
	clearApprovalRequestOptimistically: () => void;
	startApproval: (variables: StartApprovalVariables) => Promise<void>;
	updateApprovalStatus: (variables: UpdateApprovalStatusVariables) => Promise<void>;
	addApprover: (variables: AddApproverVariables) => Promise<void>;
	removeApprover: (variables: RemoveApproverVariables) => Promise<void>;
	cancelApproval: (variables: CancelApprovalVariables) => Promise<void>;
	resolveApprovalAgent: () => Promise<string>;
};

// Default context values
const defaultContextValue: ApprovalsContextValue = {
	approvalRequest: null,
	approvalRequestLoading: false,
	availableWorkflowStatuses: [],
	contentId: '',
	contentStatus: CURRENT_CONTENT_STATUS,
	isCurrentUserRequestor: false,
	isCurrentUserApprover: false,
	currentUserApprover: null,
	approvalProgress: { current: 0, total: 0 },
	hasEditPermission: false,
	isDraftLockedForEditing: false,
	otherModeReviewStatus: null,
};

const defaultDispatchValue: ApprovalsDispatchContextValue = {
	createWorkflowApplication: async () => {
		throw new Error('createWorkflowApplication not implemented');
	},
	cancelWorkflowApplication: async () => {
		throw new Error('cancelWorkflowApplication not implemented');
	},
	clearApprovalRequestOptimistically: () => {},
	startApproval: async () => {
		throw new Error('startApproval not implemented');
	},
	updateApprovalStatus: async () => {
		throw new Error('updateApprovalStatus not implemented');
	},
	addApprover: async () => {
		throw new Error('addApprover not implemented');
	},
	removeApprover: async () => {
		throw new Error('removeApprover not implemented');
	},
	cancelApproval: async () => {
		throw new Error('cancelApproval not implemented');
	},
	resolveApprovalAgent: async () => '',
};

// Create contexts
export const ApprovalsContext = createContext<ApprovalsContextValue>(defaultContextValue);
ApprovalsContext.displayName = 'ApprovalsContext';

export const ApprovalsDispatchContext =
	createContext<ApprovalsDispatchContextValue>(defaultDispatchValue);
ApprovalsDispatchContext.displayName = 'ApprovalsDispatchContext';

// Provider props
type ApprovalsProviderProps = {
	children: ReactNode;
};

type ApprovalsProviderComponentProps = ApprovalsProviderProps & {
	contentId: string;
};

/**
 * DefaultApprovalsProvider - Returns default/empty approval context
 */
const DefaultApprovalsProvider: FC<ApprovalsProviderProps> = ({ children }) => (
	<ApprovalsContext.Provider value={defaultContextValue}>
		<ApprovalsDispatchContext.Provider value={defaultDispatchValue}>
			{children}
		</ApprovalsDispatchContext.Provider>
	</ApprovalsContext.Provider>
);

/**
 * ApprovalsProvider - Safe wrapper with validation and kill switch
 * @example
 * ```tsx
 * <ApprovalsProvider>
 *   <BylineApprovalStatus />
 *   <ApprovalsPane />
 * </ApprovalsProvider>
 * ```
 */
export const ApprovalsProvider: FC<ApprovalsProviderProps> = ({ children }) => {
	const { cloudId } = useSessionData();
	const [contentId] = usePageContentId();

	if (!cloudId || !contentId || !fg('confluence_approvals_fg')) {
		return <DefaultApprovalsProvider>{children}</DefaultApprovalsProvider>;
	}

	return <ApprovalsProviderComponent contentId={contentId}>{children}</ApprovalsProviderComponent>;
};

/**
 * This provider:
 * 1. Fetches approval data using GraphQL query
 * 2. Exposes data through read context (ApprovalsContext)
 * 3. Exposes mutations through write context (ApprovalsDispatchContext)
 * 4. Handles cache updates and refetching after mutations
 */
const ApprovalsProviderComponent: FC<ApprovalsProviderComponentProps> = ({
	children,
	contentId,
}) => {
	const { cloudId, userId } = useSessionData();
	const [{ contentType }] = usePageState();
	const pageMode = useGetPageMode();
	const contentStatus: GraphQLContentStatus =
		pageMode === PageMode.EDIT ? DRAFT_CONTENT_STATUS : CURRENT_CONTENT_STATUS;
	// The content status that is opposite of the current status
	const otherContentStatus =
		contentStatus === DRAFT_CONTENT_STATUS ? CURRENT_CONTENT_STATUS : DRAFT_CONTENT_STATUS;

	const { close: closeRightSidebar } = useRightSidebarContext();

	const [isOptimisticallyCancelled, setIsOptimisticallyCancelled] = useState(false);
	const [optimisticWorkflowStatus, setOptimisticWorkflowStatus] = useState<WorkflowStatus | null>(
		null,
	);

	// Track whether we've received data for the current contentId. This ensures
	// approvalRequestLoading stays true from the moment contentId changes until fresh data
	// has actually arrived — even if useQueryInteropRelay's loading flag flips true→false
	// within a single React batch (e.g. when the Relay store has cached normalized data).
	const lastSettledContentIdRef = useRef<string | null>(null);

	// Reset optimistic state when navigating to a different page or switching content status (View ↔ Edit).
	useEffect(() => {
		setIsOptimisticallyCancelled(false);
		setOptimisticWorkflowStatus(null);
	}, [contentId, contentStatus]);

	const canEdit = useCanPerformContentOperation({
		contentId,
		operation: OperationTypes.UPDATE,
		status: contentStatus === CURRENT_CONTENT_STATUS ? CURRENT_STATUS : DRAFT_STATUS,
	});
	const hasEditPermission = canEdit ?? false;

	// Query for available workflow statuses and active workflow application
	const {
		data: approvalsData,
		loading: approvalsLoading,
		refetch: refetchApprovalsData,
	} = useQueryInterop<
		ApprovalsContextApprovalsDataQuery$data,
		ApprovalsContextApprovalsDataQuery$variables
	>(approvalsDataQuery, {
		variables: {
			cloudId,
			contentId,
			contentStatus,
			transitionType: null, // null returns all statuses
			otherContentStatus,
		},
		skip: !cloudId || !contentId || !expValEquals('confluence_approvals_eap', 'isEnabled', true),
	});

	// Detect when approvalsData belongs to a previously-viewed page. useQueryInteropRelay keeps old
	// data in state while a refetch with new variables is in flight, so without this guard the UI
	// would render Page A's fully-interactive workflow/review data while viewing Page B.
	const isDataStale = useMemo(() => {
		const responseContentId =
			approvalsData?.confluence_getActiveWorkflowApplication?.workflowApplication?.contentId;
		// If the response contains a workflow application for a different page, data is stale.
		// When responseContentId is null/undefined/empty (no active workflow or falsy defaults),
		// there is nothing stale to hide.
		return !!responseContentId && responseContentId !== contentId;
	}, [approvalsData, contentId]);

	if (!approvalsLoading && !isDataStale) {
		lastSettledContentIdRef.current = contentId;
	}

	const isAwaitingFreshData = lastSettledContentIdRef.current !== contentId;

	const availableWorkflowStatuses = useMemo(() => {
		if (isDataStale || !approvalsData?.confluence_contentApprovalStatuses) return [];
		return approvalsData.confluence_contentApprovalStatuses.reduce<WorkflowStatus[]>(
			(acc, status) => {
				if (status === null || status === undefined) return acc;
				acc.push({
					id: status.id || '',
					name: status.displayProperties?.name || '',
					allowsReviews: status.allowsReviews || false,
					color: status.displayProperties?.color || undefined,
				});
				return acc;
			},
			[],
		);
	}, [isDataStale, approvalsData]);

	const contentVersion = useMemo(() => {
		const fromWorkflow =
			approvalsData?.confluence_getActiveWorkflowApplication?.workflowApplication?.contentVersion;
		if (fromWorkflow !== undefined && fromWorkflow !== null) {
			return fromWorkflow;
		}
		return approvalsData?.content?.nodes?.[0]?.version?.number ?? 0;
	}, [approvalsData]);

	useEffect(() => {
		if (
			optimisticWorkflowStatus &&
			approvalsData?.confluence_getActiveWorkflowApplication?.workflowApplication
				?.workflowStatusId === optimisticWorkflowStatus.id
		) {
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
			setOptimisticWorkflowStatus(null);
		}
	}, [approvalsData, optimisticWorkflowStatus]);

	const approvalRequest = useMemo((): ApprovalRequest | null => {
		if (isDataStale) return null;
		if (isOptimisticallyCancelled) return null;

		const application = approvalsData?.confluence_getActiveWorkflowApplication?.workflowApplication;

		if (optimisticWorkflowStatus && application?.workflowStatusId !== optimisticWorkflowStatus.id) {
			return {
				application: {
					contentId,
					contentVersion,
					contentStatus,
					applicationId: '',
					isActive: true,
					workflowStatusId: optimisticWorkflowStatus.id,
				},
				review: null,
			};
		}

		const review =
			approvalsData?.confluence_getActiveWorkflowApplication?.workflowReviewWithDecisions;
		if (!application) return null;
		return {
			application: {
				contentId: application.contentId || contentId,
				contentVersion: application.contentVersion || 1,
				contentStatus: application.contentStatus || '', // NOTE: This indicates draft (DRAFT) or published (CURRENT)
				applicationId: application.id || '',
				isActive: application.isActive || false,
				workflowStatusId: application.workflowStatusId || '',
				...(application.modifier && {
					applicationLastModifiedBy: {
						displayName: application.modifier.displayName || '',
						avatarUrl: application.modifier.photos?.find((p) => p?.isPrimary)?.value || undefined,
						userId: application.modifier.id || '',
					},
				}),
				applicationLastModifiedDate: application.lastModificationDate?.value || '',
			},
			// There can be an application without a review; return review: null in that case
			review:
				review !== null && review !== undefined
					? {
							reviewId: review.id || '',
							reviewCreationDate: review.creationDate?.value || '',
							requestorId: review.creatorAaid || '',
							requestorName: review.creator?.displayName || '',
							requestorAvatarUrl: review.creator?.photos?.find((p) => p?.isPrimary)?.value || '',
							reviewLastModifiedDate: review.reviewLastModified?.value || '', // milliseconds since epoch
							reviewComment: review.reviewComment || undefined,
							reviewStatus: review.reviewStatus || 'PENDING',
							approvers: review.reviewerDecisions?.reduce<Approver[]>((acc, reviewerDecision) => {
								if (reviewerDecision == null) return acc;
								acc.push({
									decisionId: reviewerDecision.decisionId || '',
									reviewerId: reviewerDecision.reviewerId || '',
									isRequired: reviewerDecision.required ?? true,
									principalId: reviewerDecision.principalId || '',
									principalType: reviewerDecision.principalType || 'USER',
									name: reviewerDecision.user?.displayName || '',
									avatarUrl: reviewerDecision.user?.photos?.find((p) => p?.isPrimary)?.value || '',
									status: reviewerDecision.reviewerDecisionStatus || 'PENDING_REVIEW',
									decisionComment: reviewerDecision.comment || '',
									responseLastModifiedDate: reviewerDecision?.lastModifiedDate?.value || '',
								});
								return acc;
							}, []),
						}
					: null,
		};
	}, [
		isDataStale,
		isOptimisticallyCancelled,
		optimisticWorkflowStatus,
		contentVersion,
		contentStatus,
		approvalsData?.confluence_getActiveWorkflowApplication?.workflowApplication,
		approvalsData?.confluence_getActiveWorkflowApplication?.workflowReviewWithDecisions,
		contentId,
	]);

	const isCurrentUserRequestor = useMemo(() => {
		return approvalRequest?.review?.requestorId === userId;
	}, [approvalRequest, userId]);

	const currentUserApprover = useMemo(() => {
		return (
			approvalRequest?.review?.approvers?.find((approver) => approver.principalId === userId) ||
			null
		);
	}, [approvalRequest, userId]);

	const onWorkflowApplicationChanged = useCallback(
		(
			_eventName: string,
			response: { workflowApplicationChanged?: ConfluenceWorkflowApplicationChanged },
		) => {
			const eventData = response?.workflowApplicationChanged;
			const eventContentStatus = eventData?.contentStatus;
			if (eventContentStatus !== contentStatus) {
				return;
			}
			void refetchApprovalsData({
				cloudId,
				contentId,
				contentStatus,
				transitionType: null,
				otherContentStatus,
			});
		},
		[refetchApprovalsData, cloudId, contentId, contentStatus, otherContentStatus],
	);

	// Subscribe to real-time approval status changes (from backend workflowApplicationChanged events)
	useSubscriptionEvent({
		subscriberId: 'ApprovalsProviderWorkflowApplicationChanged',
		delta: 'workflowApplicationChanged',
		contentId,
		contentType: contentType || 'page',
		skip: !expValEquals('confluence_approvals_eap', 'isEnabled', true),
		onDataReceived: onWorkflowApplicationChanged,
	});

	const approvalProgress = useMemo(() => {
		if (!approvalRequest) return { current: 0, total: 0 };
		const responded = approvalRequest.review?.approvers?.filter(
			(a) => a.status !== null && a.status !== 'PENDING_REVIEW',
		).length;
		return {
			current: responded ?? 0,
			total: approvalRequest.review?.approvers?.length ?? 0,
		};
	}, [approvalRequest]);

	// GraphQL Mutations
	const [createWorkflowApplicationMutationFn] = useMutationInterop<
		ApprovalsContextCreateWorkflowApplicationMutation$data,
		ApprovalsContextCreateWorkflowApplicationMutation$variables
	>(createWorkflowApplicationMutation);

	const [createWorkflowReviewMutationFn] = useMutationInterop<
		ApprovalsContextCreateWorkflowReviewMutation$data,
		ApprovalsContextCreateWorkflowReviewMutation$variables
	>(createWorkflowReviewMutation, {
		refetchQueries: [
			{
				query: approvalsDataQuery,
				variables: {
					cloudId,
					contentId,
					contentStatus,
					transitionType: null,
					otherContentStatus,
				},
			},
		],
	});

	const [updateWorkflowApplicationMutationFn] = useMutationInterop<
		ApprovalsContextUpdateWorkflowApplicationMutation$data,
		ApprovalsContextUpdateWorkflowApplicationMutation$variables
	>(updateWorkflowApplicationMutation, {
		refetchQueries: [
			{
				query: approvalsDataQuery,
				variables: {
					cloudId,
					contentId,
					contentStatus,
					transitionType: null,
					otherContentStatus,
				},
			},
		],
	});

	const [updateReviewerDecisionMutationFn] = useMutationInterop<
		ApprovalsContextUpdateReviewerDecisionMutation$data,
		ApprovalsContextUpdateReviewerDecisionMutation$variables
	>(updateReviewerDecisionMutation, {
		refetchQueries: [
			{
				query: approvalsDataQuery,
				variables: {
					cloudId,
					contentId,
					contentStatus,
					transitionType: null,
					otherContentStatus,
				},
			},
		],
	});

	const [updateWorkflowReviewMutationFn] = useMutationInterop<
		ApprovalsContextUpdateWorkflowReviewMutation$data,
		ApprovalsContextUpdateWorkflowReviewMutation$variables
	>(updateWorkflowReviewMutation, {
		refetchQueries: [
			{
				query: approvalsDataQuery,
				variables: {
					cloudId,
					contentId,
					contentStatus,
					transitionType: null,
					otherContentStatus,
				},
			},
		],
	});

	const [removeContentApprovalReviewerMutationFn] = useMutationInterop<
		ApprovalsContextRemoveContentApprovalReviewerMutation$data,
		ApprovalsContextRemoveContentApprovalReviewerMutation$variables
	>(removeContentApprovalReviewerMutation, {
		refetchQueries: [
			{
				query: approvalsDataQuery,
				variables: {
					cloudId,
					contentId,
					contentStatus,
					transitionType: null,
					otherContentStatus,
				},
			},
		],
	});

	const [resolveApprovalAgentMutationFn] = useMutationInterop<
		{
			confluence_resolveApprovalAgent: {
				success: boolean;
				agent: { id: string } | null;
				errors: { message: string }[];
			} | null;
		},
		{ cloudId: string }
	>(resolveApprovalAgentMutation);

	// Wrapped mutation functions
	// Resolves the approval agent ID. If the mutation fails, returns an empty string
	// so the caller can fall back to invoking regular Rovo instead of the agent.
	const resolveApprovalAgent = useCallback(async (): Promise<string> => {
		try {
			const result = await resolveApprovalAgentMutationFn({ variables: { cloudId } });
			const response = result?.data?.confluence_resolveApprovalAgent;
			if (!response?.success || response.errors?.length > 0) {
				return '';
			}
			return response.agent?.id ?? '';
		} catch {
			return '';
		}
	}, [cloudId, resolveApprovalAgentMutationFn]);

	const createWorkflowApplication = useCallback(
		async (status: WorkflowStatus): Promise<CreateWorkflowApplicationResult> => {
			setIsOptimisticallyCancelled(false);
			setOptimisticWorkflowStatus(status);

			try {
				const result = await createWorkflowApplicationMutationFn({
					variables: {
						cloudId,
						createWorkflowApplicationRequestInput: {
							contentId,
							contentStatus,
							version: contentStatus === CURRENT_CONTENT_STATUS ? contentVersion : 1,
							workflowId: 1,
							workflowStatusId: 1, // hardcoded -- backend assigns status on creation
							isActive: true,
						},
					},
				});

				const workflowStatus = result.data?.confluence_createWorkflowApplication?.workflowStatus;
				return {
					workflowStatusId: workflowStatus?.id ?? '1',
					name: workflowStatus?.displayProperties?.name || '',
					allowsReviews: workflowStatus?.allowsReviews || false,
				};
			} catch (error) {
				setOptimisticWorkflowStatus(null);
				throw error;
			}
		},
		[cloudId, contentId, contentStatus, contentVersion, createWorkflowApplicationMutationFn],
	);

	/** Variables are optional: if not provided, this will cancel the workflow application for
	the latest content version for the current content status */
	const cancelWorkflowApplication = useCallback(
		async (variables?: Omit<UpdateWorkflowApplicationVariables, 'isActive'>): Promise<void> => {
			closeRightSidebar(APPROVALS_PANE_ID);

			try {
				await updateWorkflowApplicationMutationFn({
					variables: {
						cloudId,
						updateWorkflowApplicationRequestInput: {
							contentId: variables?.contentId
								? parseInt(variables.contentId, 10)
								: parseInt(contentId, 10),
							contentStatus: (variables?.contentStatus ?? contentStatus) as GraphQLContentStatus,
							version:
								variables?.version ??
								(contentStatus === CURRENT_CONTENT_STATUS ? contentVersion : 1),
							workflowStatusId: parseInt(
								variables?.workflowStatusId ??
									approvalRequest?.application?.workflowStatusId ??
									'1',
								10,
							),
							isActive: false,
							workflowId: 1,
						},
					},
				});
			} catch (error) {
				setIsOptimisticallyCancelled(false);
				markErrorAsHandled(error);
				// TODO: Add error handling
			}
		},
		[
			cloudId,
			contentId,
			contentStatus,
			contentVersion,
			approvalRequest?.application?.workflowStatusId,
			updateWorkflowApplicationMutationFn,
			closeRightSidebar,
		],
	);

	const clearApprovalRequestOptimistically = useCallback(() => {
		setIsOptimisticallyCancelled(true);
		setOptimisticWorkflowStatus(null);
	}, []);

	const startApproval = useCallback(
		async (variables: StartApprovalVariables) => {
			await createWorkflowReviewMutationFn({
				variables: {
					cloudId,
					createContentApprovalRequestInput: {
						contentId,
						contentStatus,
						version: contentStatus === CURRENT_CONTENT_STATUS ? contentVersion : 1, // DRAFT version is always 1,
						comment: variables.message,
						reviewers: variables.approvers,
					},
				},
			});
		},
		[cloudId, contentId, contentStatus, contentVersion, createWorkflowReviewMutationFn],
	);

	const updateApprovalStatus = useCallback(
		async (variables: UpdateApprovalStatusVariables) => {
			const decisionStatus = variables.status;

			await updateReviewerDecisionMutationFn({
				variables: {
					cloudId,
					confluenceUpdateReviewerDecisionInput: {
						reviewerId: parseInt(variables.reviewerId, 10),
						decisionStatus,
						comment: variables.comment || null,
					},
				},
			});
		},
		[cloudId, updateReviewerDecisionMutationFn],
	);

	const addApprover = useCallback(
		async (variables: AddApproverVariables) => {
			if (!approvalRequest?.application || !approvalRequest?.review) {
				throw new Error('approval request with review is required');
			}

			const existingReviewers = approvalRequest.review.approvers.map((a) => ({
				principalId: a.principalId,
				principalType: (a.principalType === 'GROUP' ? 'GROUP' : 'USER') as PrincipalType,
				isRequired: a.isRequired,
			}));
			const existingIds = new Set(existingReviewers.map((r) => r.principalId));
			const mergedReviewers = [...existingReviewers];
			for (const r of variables.approvers) {
				if (!existingIds.has(r.principalId)) {
					mergedReviewers.push({
						principalId: r.principalId,
						principalType: r.principalType,
						isRequired: r.isRequired,
					});
					existingIds.add(r.principalId);
				}
			}

			await updateWorkflowReviewMutationFn({
				variables: {
					cloudId,
					updateContentApprovalGraphQLInput: {
						workflowApplicationId: parseInt(approvalRequest.application.applicationId, 10),
						contentApprovalReviewId: parseInt(approvalRequest.review.reviewId, 10),
						reviewers: mergedReviewers.map((a) => ({
							principalId: a.principalId,
							principalType: a.principalType === 'GROUP' ? 'GROUP' : 'USER',
							isRequired: a.isRequired,
						})),
					},
				},
			});
		},
		[approvalRequest, cloudId, updateWorkflowReviewMutationFn],
	);

	const removeApprover = useCallback(
		async (variables: RemoveApproverVariables) => {
			if (!approvalRequest?.application || !approvalRequest?.review) {
				throw new Error('approval request with review is required');
			}

			const approver = approvalRequest.review.approvers.find(
				(a) => a.principalId === variables.principalId,
			);
			if (!approver) {
				throw new Error('Approver not found in current review');
			}

			await removeContentApprovalReviewerMutationFn({
				variables: {
					cloudId,
					removeContentApprovalReviewerInput: {
						workflowApplicationId: approvalRequest.application.applicationId,
						contentApprovalReviewId: approvalRequest.review.reviewId,
						principalId: variables.principalId,
						principalType: approver.principalType,
					},
				},
			});
		},
		[approvalRequest, cloudId, removeContentApprovalReviewerMutationFn],
	);

	const cancelApproval = useCallback(
		async (variables: CancelApprovalVariables) => {
			closeRightSidebar(APPROVALS_PANE_ID);

			await updateWorkflowReviewMutationFn({
				variables: {
					cloudId,
					updateContentApprovalGraphQLInput: {
						workflowApplicationId: parseInt(variables.workflowApplicationId, 10),
						contentApprovalReviewId: parseInt(variables.contentApprovalReviewId, 10),
						approvalStatus: 'CANCELED',
					},
				},
			});
		},
		[cloudId, updateWorkflowReviewMutationFn, closeRightSidebar],
	);

	const otherModeReviewStatus = useMemo((): ApprovalStatus | null => {
		if (isDataStale) return null;
		return (
			approvalsData?.confluence_getActiveWorkflowApplicationForOtherStatus
				?.workflowReviewWithDecisions?.reviewStatus ?? null
		);
	}, [isDataStale, approvalsData?.confluence_getActiveWorkflowApplicationForOtherStatus]);

	const isDraftLockedForEditing = useMemo(() => {
		if (contentStatus !== DRAFT_CONTENT_STATUS) return false;
		const review = approvalRequest?.review;
		if (review == null) return false;
		return (
			review.reviewStatus === 'PENDING' ||
			review.reviewStatus === 'APPROVED' ||
			review.reviewStatus === 'DENIED'
		);
	}, [contentStatus, approvalRequest?.review]);

	// Context values - memoized to prevent unnecessary re-renders
	const contextValue = useMemo<ApprovalsContextValue>(
		() => ({
			approvalRequest,
			approvalRequestLoading: approvalsLoading || isDataStale || isAwaitingFreshData,
			contentVersion,
			availableWorkflowStatuses,
			contentId,
			contentStatus,
			isCurrentUserRequestor,
			isCurrentUserApprover: !!currentUserApprover,
			currentUserApprover,
			approvalProgress,
			hasEditPermission,
			isDraftLockedForEditing,
			otherModeReviewStatus,
		}),
		[
			approvalRequest,
			approvalsLoading,
			isDataStale,
			isAwaitingFreshData,
			contentVersion,
			availableWorkflowStatuses,
			contentId,
			contentStatus,
			isCurrentUserRequestor,
			currentUserApprover,
			approvalProgress,
			hasEditPermission,
			isDraftLockedForEditing,
			otherModeReviewStatus,
		],
	);

	const dispatchValue = useMemo<ApprovalsDispatchContextValue>(
		() => ({
			createWorkflowApplication,
			cancelWorkflowApplication,
			clearApprovalRequestOptimistically,
			startApproval,
			updateApprovalStatus,
			addApprover,
			removeApprover,
			cancelApproval,
			resolveApprovalAgent,
		}),
		[
			createWorkflowApplication,
			cancelWorkflowApplication,
			clearApprovalRequestOptimistically,
			startApproval,
			updateApprovalStatus,
			addApprover,
			removeApprover,
			cancelApproval,
			resolveApprovalAgent,
		],
	);

	return (
		<ApprovalsContext.Provider value={contextValue}>
			<ApprovalsDispatchContext.Provider value={dispatchValue}>
				{children}
				{expValEquals('confluence_approvals_eap', 'isEnabled', true) && (
					<QuickReloadSubscriber
						name="approvals"
						types={[ReloadType.content]}
						reload={refetchApprovalsData}
					/>
				)}
			</ApprovalsDispatchContext.Provider>
		</ApprovalsContext.Provider>
	);
};

/**
 * Hook to access approval data (read-only)
 *
 * @example
 * ```tsx
 * const { approvalRequest, loading, approvalProgress } = useApprovals();
 * ```
 */
export const useApprovals = (): ApprovalsContextValue => {
	return useContext(ApprovalsContext);
};

/**
 * Hook to access approval mutations and actions (write-only)
 *
 * @example
 * ```tsx
 * const { startApproval, updateApprovalStatus } = useApprovalsDispatch();
 * ```
 */
export const useApprovalsDispatch = (): ApprovalsDispatchContextValue => {
	return useContext(ApprovalsDispatchContext);
};
